import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, formatEuro, SUBSCRIBER_PLAN } from "../../../../i18n";
import { createSubscriptionCheckout } from "../../subscriptionApi";
import { MaterialIcon, CheckoutSubmitButton } from "./checkoutUi";

const StripeSubscriptionCheckout = ({ totalLabel, plan = "premium", isAlreadySubscribed }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notConfigured, setNotConfigured] = useState(false);

  const handleSubscribe = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const { checkout_url: checkoutUrl } = await createSubscriptionCheckout(plan);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      setError(t("payment.checkoutStartFailed"));
    } catch (err) {
      if (err?.status === 503) {
        setNotConfigured(true);
        setError("");
      } else {
        setError(t("payment.checkoutStartFailed"));
      }
    } finally {
      setLoading(false);
    }
  }, [t, plan]);

  if (isAlreadySubscribed) {
    return (
      <div className="rounded-lg bg-secondary-container/40 p-6 text-center">
        <MaterialIcon
          name="verified"
          filled
          className="mx-auto mb-3 text-[32px] text-secondary"
        />
        <p className="font-body-lg font-semibold text-on-surface">
          {t("payment.alreadySubscribed")}
        </p>
        <p className="mt-2 font-body-md text-on-surface-variant">
          {t("payment.alreadySubscribedHint")}
        </p>
        <Link
          to="/dashboard/client/billing"
          className="mt-4 inline-flex items-center gap-1 font-label-sm font-semibold text-secondary hover:underline"
        >
          {t("payment.manageBilling")}
          <MaterialIcon name="arrow_forward" className="text-[16px]" />
        </Link>
      </div>
    );
  }

  if (notConfigured) {
    return (
      <div className="rounded-lg border border-outline-variant/40 bg-surface-container p-6">
        <MaterialIcon
          name="schedule"
          className="mb-3 text-[28px] text-on-surface-variant"
        />
        <p className="font-body-lg font-semibold text-on-surface">
          {t("payment.paymentNotConfigured")}
        </p>
        <p className="mt-2 font-body-md text-on-surface-variant">
          {t("payment.paymentNotConfiguredHint")}
        </p>
        <Link
          to="/dashboard/client/support"
          className="mt-4 inline-flex items-center gap-1 font-label-sm font-semibold text-secondary hover:underline"
        >
          {t("payment.contactSupport")}
          <MaterialIcon name="arrow_forward" className="text-[16px]" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-surface-container p-5">
        <h3 className="font-headline-md text-headline-md text-on-surface">
          {t("payment.stripeCheckoutTitle")}
        </h3>
        <p className="mt-2 font-body-md text-on-surface-variant">
          {t("payment.stripeCheckoutDesc")}
        </p>
        <ul className="mt-4 space-y-2 font-body-md text-on-surface-variant">
          <li className="flex items-start gap-2">
            <MaterialIcon name="check_circle" className="mt-0.5 text-[18px] text-secondary" />
            {t("payment.stripeRenewalNote", {
              amount: formatEuro(SUBSCRIBER_PLAN.monthlyPrice),
            })}
          </li>
          <li className="flex items-start gap-2">
            <MaterialIcon name="check_circle" className="mt-0.5 text-[18px] text-secondary" />
            {t("payment.stripeMethodsNote")}
          </li>
          <li className="flex items-start gap-2">
            <MaterialIcon name="check_circle" className="mt-0.5 text-[18px] text-secondary" />
            {t("payment.stripeSecureNote")}
          </li>
        </ul>
      </div>

      {error ? (
        <p className="rounded-lg bg-error-container px-4 py-3 text-label-sm text-on-error-container">
          {error}
        </p>
      ) : null}

      <CheckoutSubmitButton
        type="button"
        onClick={handleSubscribe}
        submitting={loading}
      >
        {t("payment.subscribeNow", { amount: totalLabel })}
        <MaterialIcon name="open_in_new" className="text-[20px]" />
      </CheckoutSubmitButton>

      <p className="text-center text-label-sm text-on-surface-variant">
        {t("payment.stripePowered")}
      </p>
    </div>
  );
};

export default StripeSubscriptionCheckout;
